/**
 * Trim planning — pure, dependency-free math so it can be unit tested without
 * WebCodecs present.
 *
 * Two trim strategies share this planner:
 *
 *  - `fast`: lossless remux. Video starts at the keyframe at or before the in
 *    point, so the cut can land up to one keyframe interval early.
 *  - `precise`: decode from that same keyframe, throw away the frames before
 *    the in point, and re-encode. The cut lands exactly where the handle sits.
 */

export interface ChunkLike {
  timestamp: number;
  duration: number;
  type: 'key' | 'delta';
}

export interface TrimPlan {
  /** First video chunk that must be fed to the pipeline (index into chunks). */
  videoStartIndex: number;
  /** Exclusive end index for video chunks. */
  videoEndIndex: number;
  /** Timestamp of the first video chunk in the output; this becomes 0. */
  videoOriginUs: number;
  /** First audio chunk that overlaps the range. */
  audioStartIndex: number;
  /** Exclusive end index for audio chunks. */
  audioEndIndex: number;
  /** Subtracted from every audio timestamp so both tracks share an origin. */
  audioOriginUs: number;
  /** True when the in point sits exactly on a keyframe. */
  startsOnKeyframe: boolean;
  /** How much earlier than the requested in point the output actually starts. */
  leadInUs: number;
  /** Duration of the planned output. */
  durationUs: number;
  /** True when the plan covers the entire recording. */
  isWholeRecording: boolean;
}

export function indexKeyframes(chunks: ChunkLike[]): number[] {
  const indices: number[] = [];
  for (let i = 0; i < chunks.length; i += 1) {
    if (chunks[i].type === 'key') indices.push(i);
  }
  return indices;
}

/** Index of the last keyframe at or before `timestamp`. */
export function keyframeIndexAtOrBefore(chunks: ChunkLike[], keyframes: number[], timestamp: number): number {
  if (keyframes.length === 0) return 0;
  let low = 0;
  let high = keyframes.length - 1;
  let result = keyframes[0];
  while (low <= high) {
    const mid = (low + high) >> 1;
    const index = keyframes[mid];
    if (chunks[index].timestamp <= timestamp) {
      result = index;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return result;
}

/** Index of the last chunk whose timestamp is <= `timestamp`. */
export function chunkIndexAtOrBefore(chunks: ChunkLike[], timestamp: number): number {
  if (chunks.length === 0) return 0;
  let low = 0;
  let high = chunks.length - 1;
  let result = 0;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (chunks[mid].timestamp <= timestamp) {
      result = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return result;
}

/** First chunk that ends after `timestamp` (i.e. the one playing at that moment). */
export function chunkIndexContaining(chunks: ChunkLike[], timestamp: number): number {
  const candidate = chunkIndexAtOrBefore(chunks, timestamp);
  const chunk = chunks[candidate];
  if (chunk && timestamp >= chunk.timestamp + Math.max(chunk.duration, 1)) {
    return Math.min(candidate + 1, Math.max(chunks.length - 1, 0));
  }
  return candidate;
}

export function recordingDurationUs(chunks: ChunkLike[]): number {
  if (chunks.length === 0) return 0;
  const last = chunks[chunks.length - 1];
  return last.timestamp + Math.max(last.duration, 0);
}

export interface PlanTrimInput {
  videoChunks: ChunkLike[];
  keyframes?: number[];
  audioChunks?: ChunkLike[];
  inUs: number;
  outUs: number;
  /** `fast` snaps the in point back to a keyframe; `precise` does not. */
  mode: 'fast' | 'precise';
}

export function planTrim(input: PlanTrimInput): TrimPlan {
  const { videoChunks, inUs, outUs, mode } = input;
  const audioChunks = input.audioChunks ?? [];
  const keyframes = input.keyframes ?? indexKeyframes(videoChunks);
  const totalUs = recordingDurationUs(videoChunks);

  if (videoChunks.length === 0) {
    return {
      videoStartIndex: 0,
      videoEndIndex: 0,
      videoOriginUs: 0,
      audioStartIndex: 0,
      audioEndIndex: 0,
      audioOriginUs: 0,
      startsOnKeyframe: true,
      leadInUs: 0,
      durationUs: 0,
      isWholeRecording: true,
    };
  }

  const start = Math.min(Math.max(inUs, 0), totalUs);
  const end = Math.min(Math.max(outUs, start), totalUs);

  const keyframeIndex = keyframeIndexAtOrBefore(videoChunks, keyframes, start);
  const videoStartIndex = mode === 'fast' ? keyframeIndex : keyframeIndex;
  const videoOriginUs = videoChunks[videoStartIndex].timestamp;
  const startsOnKeyframe = videoChunks[videoStartIndex].timestamp === start;

  // The encoder drops anything before the in point in precise mode, so the
  // output clock starts at the requested in point rather than the keyframe.
  const outputOriginUs = mode === 'fast' ? videoOriginUs : start;

  let videoEndIndex = videoChunks.length;
  for (let i = videoStartIndex; i < videoChunks.length; i += 1) {
    if (videoChunks[i].timestamp >= end) {
      videoEndIndex = Math.max(i, videoStartIndex + 1);
      break;
    }
  }

  let audioStartIndex = 0;
  let audioEndIndex = audioChunks.length;
  if (audioChunks.length > 0) {
    audioStartIndex = chunkIndexContaining(audioChunks, outputOriginUs);
    audioEndIndex = audioChunks.length;
    for (let i = audioStartIndex; i < audioChunks.length; i += 1) {
      if (audioChunks[i].timestamp >= end) {
        audioEndIndex = Math.max(i, audioStartIndex + 1);
        break;
      }
    }
  }

  const durationUs = Math.max(end - outputOriginUs, 0);
  return {
    videoStartIndex,
    videoEndIndex,
    videoOriginUs: outputOriginUs,
    audioStartIndex,
    audioEndIndex,
    audioOriginUs: outputOriginUs,
    startsOnKeyframe,
    // In fast mode the cut starts at the keyframe *before* the in point, so the
    // output carries a little head that the requested range did not include.
    leadInUs: Math.max(start - outputOriginUs, 0),
    durationUs,
    isWholeRecording: videoStartIndex === 0 && videoEndIndex === videoChunks.length,
  };
}

/**
 * The in point a `fast` export will really use, so the UI can warn about the
 * tiny head that gets included instead of silently changing the result.
 */
export function effectiveInPoint(videoChunks: ChunkLike[], keyframes: number[], inUs: number): number {
  if (videoChunks.length === 0) return 0;
  return videoChunks[keyframeIndexAtOrBefore(videoChunks, keyframes, inUs)].timestamp;
}

export function snapToFrame(timestampUs: number, frameUs: number): number {
  return Math.round(timestampUs / frameUs) * frameUs;
}
